import { useSelector } from "react-redux";
import {
  downloadInvoiceFile,
  getDetails,
  resendServiceChargeInvoiceEmails,
  resendServiceChargeInvoicePrints,
  resetDetails,
  selectEmailProgressResponse,
  selectIsEmailing,
  selectIsPrinting,
  selectLoadingDetails,
  selectPrintProgressResponse,
  selectServiceChargeDetails,
  setIsEmailing,
  setPrintProgressResponse,
} from "./ServiceChargeInvoicingSummariesSlice";
import { useCallback, useEffect, useRef, useState } from "react";
import { useThunkDispatch } from "setup/redux/thunkDispatch";
import "./ServiceChargeInvoicingStyles.scss";
import { useDispatch } from "react-redux";
import { Field, Form, Formik } from "formik";
import { ServiceChargeInvoiceBreakdownModel, ServiceChargeInvoiceDetailsModel } from "services/api/cplapi/CPLAPI";
import { createColumnHelper } from "@tanstack/table-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/pro-solid-svg-icons";
import CPLTable from "cpl-react-common/components/Tables/CPLTable";
import { ActivityIndicator } from "cpl-react-common/components/ActivityIndicators/ActivityIndicator";
import { getDeliveryOption, getDeliveryStatusNode, getDeliveryStatusText } from "../ServiceChargeInvoicingHelpers";
import CSVExport from "cpl-react-common/components/Actions/CSVExport";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { numberSearchingFn } from "cpl-react-common/components/Tables/TableFilterFunctions";
import { OKAlert } from "cpl-react-common/components/Alerts/Okay";
import { Style } from "cpl-react-common/Helpers/AlertHelpers";
import { setEmailProgressResponse } from "./steps/InvoiceSending/InvoiceSendingSlice";

type Props = {
  logID: number;
};

const ServiceChargeInvoicingDetails: React.FC<Props> = ({ logID }: Props) => {
  const dispatch = useDispatch();
  const thunkDispatch = useThunkDispatch();
  const details = useSelector(selectServiceChargeDetails);
  const loadingDetails = useSelector(selectLoadingDetails);
  const anchorRef = useRef<HTMLAnchorElement>(null);
  const [breakdownsToEmail, setBreakdownsToEmail] = useState<number[]>([]);
  const [breakdownsToReprint, setBreakdownsToReprint] = useState<number[]>([]);
  const printProgressResponse = useSelector(selectPrintProgressResponse);
  const emailProgressResponse = useSelector(selectEmailProgressResponse);
  const IsEmailing = useSelector(selectIsEmailing);
  const isPrinting = useSelector(selectIsPrinting);
  const [showPrintAlert, setShowPrintAlert] = useState<boolean>(false);
  const [showEmailAlert, setShowEmailAlert] = useState<boolean>(false);

  useEffect(() => {
    if (logID !== undefined) {
      thunkDispatch(getDetails(logID));
    }
    return function () {
      dispatch(resetDetails());
    };
  }, [thunkDispatch, dispatch, logID]);

  const columnHelper = createColumnHelper<ServiceChargeInvoiceBreakdownModel>();
  const columnDefinition = [
    columnHelper.accessor((row) => row.clientName, {
      id: "Client",
      cell: (info) => info.getValue(),
      header: () => <>Client</>,
      meta: {
        columnClassName: "showTableCellOnTabletAndDesktop",
      },
    }),
    columnHelper.accessor((row) => row.accountNumber, {
      id: "AccountNumber",
      cell: (info) => info.getValue(),
      header: () => <>Account Number</>,
      filterFn: numberSearchingFn,
      meta: {
        columnClassName: "showTableCellOnTabletAndDesktop",
      },
    }),
    columnHelper.accessor((row) => row.clientName, {
      id: "ClientAndNumber",
      cell: (info) => (
        <>
          <div>{info.row.original.clientName}</div>
          <div className="text-muted">{info.row.original.accountNumber}</div>
        </>
      ),
      header: () => <>Client</>,
      meta: {
        columnClassName: "showTableCellOnMobile",
      },
    }),
    columnHelper.accessor((row) => row.invoiceAmount, {
      id: "InvoiceAmount",
      cell: (info) => `£${info.getValue()?.toString()}`,
      filterFn: numberSearchingFn,
      header: () => <>Invoice Amount</>,
      meta: {},
    }),
    columnHelper.accessor((row) => getDeliveryOption(row.shouldEmail, row.shouldPrint), {
      id: "EmailOrPrint",
      cell: (info) => info.getValue(),
      header: () => <>Email Or Print</>,
      meta: {
        columnClassName: "showTableCellOnTabletAndDesktop",
      },
    }),
    columnHelper.accessor((row) => row.developmentName, {
      id: "Development",
      cell: (info) => info.getValue(),
      header: () => <>Development</>,
      meta: {
        columnClassName: "showTableCellOnTabletAndDesktop",
      },
    }),
    columnHelper.accessor((row) => row.propertyManagerName, {
      id: "PropertyManager",
      cell: (info) => info.getValue(),
      header: () => <>Property Manager</>,
      meta: {
        columnClassName: "showTableCellOnTabletAndDesktop",
      },
    }),
    columnHelper.accessor((row) => row, {
      id: "DevelopmentAndPropertyManager",
      cell: (info) => (
        <>
          <div>{info.row.original.developmentName}</div>
          <div className="text-muted">{info.row.original.propertyManagerName}</div>
        </>
      ),
      header: () => <>Development And Property Manager</>,
      meta: {
        columnClassName: "showTableCellOnMobile",
      },
    }),
    columnHelper.accessor((row) => getDeliveryStatusText(row), {
      id: "Status",
      cell: (info) => getDeliveryStatusNode(info.row.original),
      header: () => <>Status</>,
      meta: {},
    }),
    columnHelper.accessor((row) => row.breakdownID, {
      id: "ResendEmail",
      cell: (props) => (
        <input
          data-test-id={`service-charge-invoice-details-page__resend-email-checkbox-${props.getValue()}`}
          type="checkbox"
          className="form-check-input"
          id={props.getValue() + "ResendEmailCheckbox"}
          onChange={(e) => resendEmailSelect(e.target.checked as boolean, props.getValue() as number)}
          disabled={!props.row.original.shouldEmail}
          checked={breakdownsToEmail.includes(props.getValue() as number)}
        />
      ),
      header: () => <>Email?</>,
      enableSorting: false,
      enableColumnFilter: false,
    }),
    columnHelper.accessor((row) => row.breakdownID, {
      id: "ResendPrint",
      cell: (props) => (
        <input
          data-test-id={`service-charge-invoice-details-page__resend-print-checkbox-${props.getValue()}`}
          type="checkbox"
          className="form-check-input form-control-solid"
          id={props.getValue() + "ResendPrintCheckbox"}
          onChange={(e) => resendPrintSelect(e.target.checked as boolean, props.getValue() as number)}
          disabled={!props.row.original.shouldPrint}
          checked={breakdownsToReprint.includes(props.getValue() as number)}
        />
      ),
      header: () => <>Print?</>,
      enableSorting: false,
      enableColumnFilter: false,
    }),
    columnHelper.display({
      id: "actions",
      cell: (props) => (
        <a
          data-test-id={`service-charge-invoice-details-page__view-invoice-link-${props.row.original.breakdownID}`}
          className="btn btn-icon btn-primary"
          onClick={() => {
            downloadInvoice(props.row.original.breakdownID!);
          }}
          data-bs-toggle="tooltip"
          data-bs-placement="top"
          target="_blank"
          title="View invoice"
        >
          <FontAwesomeIcon icon={faDownload} />
        </a>
      ),
      enableHiding: false,
      meta: {
        columnClassName: "showTableCellOnTabletAndDesktop",
      },
    }),
  ];

  function resendEmailSelect(selected: boolean, breakdownID: number) {
    const exists = breakdownsToEmail?.includes(breakdownID);
    if (selected && exists) return;
    if (!selected && !exists) return;
    if (selected && !exists) {
      const newBreakdownsToEmail = [breakdownID, ...breakdownsToEmail];
      setBreakdownsToEmail(newBreakdownsToEmail);
    }
    if (!selected && exists) {
      const newBreakdownsToEmail = breakdownsToEmail.filter((breakdown) => {
        return breakdown != breakdownID;
      });
      setBreakdownsToEmail(newBreakdownsToEmail);
    }
  }

  function resendPrintSelect(selected: boolean, breakdownID: number) {
    const exists = breakdownsToReprint?.includes(breakdownID);
    if (selected && exists) return;
    if (!selected && !exists) return;
    if (selected && !exists) {
      const newBreakdownsToPrint = [breakdownID, ...breakdownsToReprint];
      setBreakdownsToReprint(newBreakdownsToPrint);
      return;
    }
    if (!selected && exists) {
      const newBreakdownsToPrint = breakdownsToReprint.filter((breakdown) => {
        return breakdown != breakdownID;
      });
      setBreakdownsToReprint(newBreakdownsToPrint);
      return;
    }
  }

  async function downloadInvoice(breakdownID: number) {
    const downloadedFile = await thunkDispatch(downloadInvoiceFile(breakdownID)).unwrap();
    if (anchorRef.current !== null) {
      const fileURL = URL.createObjectURL(downloadedFile);
      anchorRef.current.href = fileURL;
      anchorRef.current.download = downloadedFile.name;
      anchorRef.current.click();

      URL.revokeObjectURL(fileURL);
    }
  }

  function resendEmails() {
    if (breakdownsToEmail != null) {
      dispatch(setIsEmailing(true));
      thunkDispatch(resendServiceChargeInvoiceEmails(breakdownsToEmail));
    }
  }

  function reprintInvoices() {
    if (breakdownsToReprint != null) {
      thunkDispatch(resendServiceChargeInvoicePrints(breakdownsToReprint));
    }
  }

  const resetPrints = useCallback(() => {
    setShowPrintAlert(false);
    setBreakdownsToReprint([]);
    dispatch(setPrintProgressResponse(undefined));
    thunkDispatch(getDetails(logID));
  }, [dispatch, logID, thunkDispatch]);

  const resetEmails = useCallback(() => {
    setShowEmailAlert(false);
    setBreakdownsToEmail([]);
    dispatch(setEmailProgressResponse(undefined));
    thunkDispatch(getDetails(logID));
  }, [dispatch, logID, thunkDispatch]);

  useEffect(() => {
    if (!printProgressResponse?.printedExternally && printProgressResponse?.previewURL != null) {
      location.href = printProgressResponse.previewURL;
      resetPrints();
    }
    if (printProgressResponse?.printedExternally) {
      setShowPrintAlert(true);
    }
  }, [dispatch, printProgressResponse, resetPrints]);

  useEffect(() => {
    if (emailProgressResponse?.finished) {
      dispatch(setIsEmailing(false));
      if ((emailProgressResponse.failedEmails ?? 0) > 0) {
        setShowEmailAlert(true);
      } else resetEmails();
    }
  }, [emailProgressResponse, dispatch, resetEmails]);

  return (
    <div className="card mb-6">
      <OKAlert
        showAlert={showPrintAlert}
        alertText={printProgressResponse?.message}
        style={Style.Info}
        alertTitle="Reprint Invoices"
        callBack={() => resetPrints()}
      ></OKAlert>
      <OKAlert
        showAlert={showEmailAlert}
        alertText={emailProgressResponse?.message}
        style={Style.Info}
        alertTitle="Resend Invoices"
        callBack={() => resetEmails()}
      ></OKAlert>
      <div className="card-header">
        <div className="card-title">Service Charge Invoice Details</div>
      </div>
      <div className="card-body">
        <ActivityIndicator isBusy={loadingDetails}>
          {/* Hidden anchor used only to programmatically trigger a file download (see downloadInvoice), not user-clicked. */}
          <a
            data-test-id="service-charge-invoice-details-page__hidden-download-link"
            ref={anchorRef}
            target="_blank"
            rel="noreferrer"
            className="file-preview-link"
          />
          <Formik
            initialValues={details != undefined ? details : new ServiceChargeInvoiceDetailsModel()}
            onSubmit={() => {}}
            enableReinitialize={true}
          >
            {({ values }) => {
              return (
                <Form>
                  <div className="row mb-6">
                    <div className="col-6">
                      <label className="form-label">Requested Date/Time</label>
                      <div>
                        <Field
                          data-test-id="service-charge-invoice-details-page__requested-date-input"
                          className="form-control form-control-solid"
                          placeholder="Requested date..."
                          name="requesteddate"
                          readOnly={true}
                          value={values.requestedDate?.toCPLCommentDateTimeString() ?? ""}
                        />
                      </div>
                    </div>
                    <div className="col-6">
                      <label className="form-label">Requested By User</label>
                      <div>
                        <Field
                          data-test-id="service-charge-invoice-details-page__requested-by-input"
                          className="form-control form-control-solid"
                          placeholder="Requested by..."
                          name="requestedby"
                          readOnly={true}
                          value={values.requestedByUser ?? ""}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="row mb-6">
                    <div className="col-6">
                      <label className="form-label">Completed Date/Time</label>
                      <div>
                        <Field
                          data-test-id="service-charge-invoice-details-page__completed-date-input"
                          className="form-control form-control-solid"
                          placeholder="Completed date..."
                          name="completedate"
                          readOnly={true}
                          value={values.completedDate?.toCPLCommentDateTimeString() ?? ""}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="row mb-6">
                    <div className="col-6">
                      <label className="form-label">Status</label>
                      <div>
                        <Field
                          data-test-id="service-charge-invoice-details-page__status-input"
                          className="form-control form-control-solid"
                          placeholder="Status..."
                          name="status"
                          readOnly={true}
                          value={values.status ?? ""}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="row mb-6">
                    <div className="col-6">
                      <label className="form-label">Property Manager(s)</label>
                      <div>
                        <Field
                          data-test-id="service-charge-invoice-details-page__property-managers-input"
                          as="textarea"
                          className="form-control form-control-solid h-100px"
                          placeholder="Property managers..."
                          name="propertymanagers"
                          readOnly={true}
                          value={values.propertyManagers?.join("\n") ?? ""}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="row mb-6">
                    <div className="col-6">
                      <label className="form-label">Development(s)</label>
                      <div>
                        <Field
                          data-test-id="service-charge-invoice-details-page__developments-input"
                          as="textarea"
                          className="form-control form-control-solid h-100px"
                          placeholder="Developments..."
                          name="developments"
                          readOnly={true}
                          value={values.developments?.join("\n") ?? ""}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="row mb-6">
                    <div className="col-6">
                      <label className="form-label">Batch Number</label>
                      <div>
                        <Field
                          data-test-id="service-charge-invoice-details-page__batch-number-input"
                          className="form-control form-control-solid"
                          placeholder="Batch number..."
                          name="batchnumber"
                          readOnly={true}
                          value={values.batchNumber ?? ""}
                        />
                      </div>
                    </div>
                  </div>
                </Form>
              );
            }}
          </Formik>
          <h2>Invoices</h2>
          <div className="d-flex justify-content-end align-items-center">
            {details?.breakdowns && (
              <>
                <ActivityIndicator isBusy={IsEmailing}>
                  <button
                    data-test-id="service-charge-invoice-details-page__resend-emails-button"
                    className="me-1 btn btn-primary"
                    onClick={resendEmails}
                    disabled={breakdownsToEmail.length == 0 || IsEmailing}
                  >
                    Resend Emails
                  </button>
                </ActivityIndicator>
                <ActivityIndicator isBusy={isPrinting}>
                  <button
                    data-test-id="service-charge-invoice-details-page__reprint-invoices-button"
                    disabled={breakdownsToReprint.length == 0 || isPrinting}
                    className="me-1 btn btn-primary"
                    onClick={reprintInvoices}
                  >
                    Reprint Invoices
                  </button>
                </ActivityIndicator>
                <CSVExport
                  data-test-id="service-charge-invoice-details-page__export-csv-button"
                  headers={Object.keys(new ServiceChargeInvoiceBreakdownModel())}
                  data={details?.breakdowns}
                  fileName={`Service Charge Invoices ${details.batchNumber} ${new Date().toCPLDateTimeString()}`}
                  applyStyles={false}
                >
                  <div className="fs-4">
                    <span className="me-2">Export</span>
                    <FontAwesomeIcon icon={faDownload as IconProp} />
                  </div>
                </CSVExport>
              </>
            )}
          </div>
          <CPLTable
            data={details?.breakdowns}
            columns={columnDefinition}
            sortingState={[{ id: "Client", desc: false }]}
            pagination={true}
            rowMobileClickAction={() => {}}
          />
        </ActivityIndicator>
      </div>
    </div>
  );
};

export default ServiceChargeInvoicingDetails;
